import { UUID } from '@macs-frontend/core/utils/uuid';
import { MdAction } from './action';
import { CompositionRole, ScheduleAssignment } from './action-team';
import { BookingPerson } from './booking-person';
import { Ratesheet } from './ratesheet.model';

export interface Contract {
  uuid: UUID;
  status: ContractStatuses;
  type: ContractTypes;
  tariff: number;
  tariffType: Ratesheet;
  studentHours: number;
  contractArt13: string;
  action: MdAction;
  bookingPerson: BookingPerson;
  compositionRole: CompositionRole;
  documentNumber: string;
  tariffForCalculation: number;
  realSchedules: ContractRealSchedule[];
  totalPlannedScheduleTime: number;
  totalRealScheduleTime: number;
  overtimeHours: number;
  normalHours: number;
  holidayHours: number;
  holidayOvertimeHours: number;
  tariffPerOvertimeHours: number;
  tariffPerNormalHours: number;
  tariffPerHolidayHours: number;
  tariffPerHolidayOvertimeHours: number;
  /**
   * Pay rates the company's payroll rules apply to this contract's date,
   * resolved server-side so the form shows what payroll will actually book.
   */
  payrollOvertimeRatePercentage: number | null;
  payrollHolidayRatePercentage: number | null;
  /** Optional so a payload cached before this field existed still calculates. */
  payrollIsHoliday?: boolean;
  homeTravelDistance: number;
  homeTravelPrice: number;
  homeTravelAmount: number;
  workTravelDistance: number;
  workTravelPrice: number;
  workTravelAmount: number;
  totalTravelAmount: number;
  expenses: ContractExpense[];
  special: string;
  advance: number;
  fine: number;
  totalTariff: number;
  totalExpensesAmount: number;
  dimonaStatus: null | DimonaStatuses;
  dimonaError: string | null;
  signed: boolean;
  realBreakHours: number;
  pointhrExportStatus: PointhrContractExportStatuses | null;
  pointhrExportError: string | null;
  jointCommitteeCode: JointCommitteeCode | null;
}

export type JointCommitteeCode = '200' | '201' | '302';

export enum DimonaStatuses {
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  SEND_ERROR = 'SEND_ERROR',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
  SENT_TO_ATTENTIA = 'SENT_TO_ATTENTIA',
  CANCEL_SENT = 'CANCEL_SENT',
  CANCEL_ERROR = 'CANCEL_ERROR',
  CANCELLED_IN_ATTENTIA = 'CANCELLED_IN_ATTENTIA',
}

export enum PointhrContractExportStatuses {
  SCHEDULED = 'SCHEDULED',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  ERROR = 'ERROR',
}

export enum ContractExpenseTypes {
  DEFAULT = 'DEFAULT',
  MEAL_COMPENSATION = 'MEAL_COMPENSATION',
}

export interface ContractExpense {
  uuid: UUID;
  name: string;
  amount: number;
  type: ContractExpenseTypes;
}

export interface ContractRealSchedule {
  uuid: UUID;
  plannedSchedule: ScheduleAssignment;
  startTime: number;
  endTime: number;
  duration: number;
}

export enum ContractTypes {
  TYPE_ATTENTIA = 'ATTENTIA',
  TYPE_INVOICE = 'INVOICE',
  TYPE_POINTHR = 'POINTHR',
}

export enum ContractStatuses {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  CANCELLED = 'CANCELLED',
  TO_REVIEW = 'TO_REVIEW',
  IN_QUESTION = 'IN_QUESTION',
  APPROVED = 'APPROVED',
  CLOSED = 'CLOSED',
  CLOSED_PAID = 'CLOSED_PAID',
  CLOSED_NOT_PAID = 'CLOSED_NOT_PAID',
  CANCEL_SENT = 'CANCEL_SENT',
}

export enum ContractFilterStatuses {
  INTEGRATION_ERROR = 'INTEGRATION_ERROR',
  DIMONA_SEND_ERROR = 'DIMONA_SEND_ERROR',
  DIMONA_CANCEL_ERROR = 'DIMONA_CANCEL_ERROR',
}

export type ContractTabStatuses = ContractStatuses | ContractFilterStatuses;

export const ContractStatusesLabels: { readonly [status: string]: string } = {
  [ContractStatuses.OPEN]: 'Open',
  [ContractStatuses.IN_PROGRESS]: 'In progress',
  [ContractStatuses.CANCELLED]: 'Cancelled',
  [ContractStatuses.TO_REVIEW]: 'To review',
  [ContractStatuses.IN_QUESTION]: 'In question',
  [ContractStatuses.APPROVED]: 'Approved',
  [ContractStatuses.CLOSED]: 'Closed',
  [ContractStatuses.CLOSED_PAID]: 'Closed paid',
  [ContractStatuses.CLOSED_NOT_PAID]: 'Closed not paid',
};

export interface ContractsCountByStatus {
  [ContractStatuses.OPEN]: number;
  [ContractStatuses.IN_PROGRESS]: number;
  [ContractStatuses.TO_REVIEW]: number;
  [ContractStatuses.IN_QUESTION]: number;
  [ContractStatuses.CLOSED]: number;
  [ContractStatuses.CLOSED_NOT_PAID]: number;
  [ContractStatuses.CLOSED_PAID]: number;
  [ContractStatuses.CANCELLED]: number;
  [ContractStatuses.APPROVED]: number;
  [ContractFilterStatuses.INTEGRATION_ERROR]: number;
  [ContractFilterStatuses.DIMONA_SEND_ERROR]: number;
  [ContractFilterStatuses.DIMONA_CANCEL_ERROR]: number;
}
